import type {
  CarnivalGameState,
  Die,
  DieId,
  LustPick,
  RevealSinAction,
  SinChoice,
  ValidationResult,
  WrathTarget,
} from "../types";
import { findDie, getUnlockedBlackDice } from "../utils/dice";
import { fail, ok, validateActor } from "./common";

function isBlack(die: Die | undefined): die is Die {
  return die !== undefined && die.color === "black";
}

function isOdd(value: number): boolean {
  return value % 2 === 1;
}

function hasMatchingPair(unlocked: readonly Die[]): boolean {
  const seen = new Set<number>();
  for (const die of unlocked) {
    if (seen.has(die.value)) return true;
    seen.add(die.value);
  }
  return false;
}

function validateGreedChoice(
  state: CarnivalGameState,
  choice: Extract<SinChoice, { sin: "greed" }>,
): ValidationResult {
  const unlocked = getUnlockedBlackDice(state.dice);
  if (choice.dieIds === null) {
    if (hasMatchingPair(unlocked))
      return fail("A matching pair is available and must be taken");
    return ok;
  }
  const [firstId, secondId] = choice.dieIds;
  if (firstId === secondId) return fail("Greed requires two distinct dice");
  const first = findDie(state.dice, firstId);
  const second = findDie(state.dice, secondId);
  if (!isBlack(first) || first.lockedBy !== null)
    return fail("First die is not unlocked on the table", { firstId });
  if (!isBlack(second) || second.lockedBy !== null)
    return fail("Second die is not unlocked on the table", { secondId });
  if (first.value !== second.value)
    return fail("Both dice must show the same value", { firstId, secondId });
  return ok;
}

function validateGluttonyChoice(
  state: CarnivalGameState,
  choice: Extract<SinChoice, { sin: "gluttony" }>,
): ValidationResult {
  const unlocked = getUnlockedBlackDice(state.dice);
  if (choice.dieId === null) {
    if (unlocked.length > 0)
      return fail("An unlocked die is available and must be taken");
    return ok;
  }
  const die = findDie(state.dice, choice.dieId);
  if (!isBlack(die) || die.lockedBy !== null)
    return fail("Die is not unlocked on the table", { dieId: choice.dieId });
  const maxValue = Math.max(...unlocked.map((d) => d.value));
  if (die.value !== maxValue)
    return fail("Die is not the highest-value unlocked die", {
      value: die.value,
      maxValue,
    });
  return ok;
}

function resolvePick(
  state: CarnivalGameState,
  pick: LustPick,
): Die | undefined {
  const die = findDie(state.dice, pick.dieId);
  if (!isBlack(die)) return undefined;
  if (pick.stolenFromPlayerId === undefined) {
    return die.lockedBy === null ? die : undefined;
  }
  return die.lockedBy?.ownerId === pick.stolenFromPlayerId ? die : undefined;
}

function anyValidLustPairExists(dice: readonly Die[]): boolean {
  const black = dice.filter((die) => die.color === "black");
  const table = black.filter((die) => die.lockedBy === null);
  for (const anchor of table) {
    for (const other of black) {
      if (other.id === anchor.id) continue;
      if (isOdd(anchor.value) !== isOdd(other.value)) return true;
    }
  }
  return false;
}

function validateLustChoice(
  state: CarnivalGameState,
  choice: Extract<SinChoice, { sin: "lust" }>,
): ValidationResult {
  if (choice.picks === null) {
    if (anyValidLustPairExists(state.dice))
      return fail("A valid odd/even pair is available and must be taken");
    return ok;
  }
  const [firstPick, secondPick] = choice.picks;
  if (firstPick.dieId === secondPick.dieId)
    return fail("Lust requires two distinct dice");
  const bothStolen =
    firstPick.stolenFromPlayerId !== undefined &&
    secondPick.stolenFromPlayerId !== undefined;
  if (bothStolen) return fail("At least one die must come from the table");

  const first = resolvePick(state, firstPick);
  const second = resolvePick(state, secondPick);
  if (!first)
    return fail("First die is not available for this pick", {
      pick: firstPick,
    });
  if (!second)
    return fail("Second die is not available for this pick", {
      pick: secondPick,
    });
  if (isOdd(first.value) === isOdd(second.value)) {
    return fail("Lust requires one odd-valued and one even-valued die");
  }
  return ok;
}

function validateWrathChoice(
  state: CarnivalGameState,
  target: WrathTarget,
): ValidationResult {
  const die = findDie(state.dice, target.dieId as DieId);
  if (!isBlack(die))
    return fail("Wrath can only target a black die", { dieId: target.dieId });
  if (target.fromPlayerId === undefined) {
    if (die.lockedBy !== null)
      return fail("Die is not on the table", { dieId: target.dieId });
    return ok;
  }
  if (die.lockedBy?.ownerId !== target.fromPlayerId) {
    return fail("Die is not locked to the named player", {
      dieId: target.dieId,
      fromPlayerId: target.fromPlayerId,
    });
  }
  return ok;
}

export function validateRevealSin(
  state: CarnivalGameState,
  action: RevealSinAction,
): ValidationResult {
  const actor = validateActor(state, action.playerId);
  if (!actor.valid) return actor;

  if (state.phase !== "REVEAL_RESOLVE") {
    return fail("Sins can only be revealed during reveal/resolve", {
      phase: state.phase,
    });
  }

  const expectedPlayerId = state.revealOrder[state.nextToRevealIndex];
  if (expectedPlayerId !== action.playerId) {
    return fail("It is not this player's turn to reveal", {
      expectedPlayerId,
      playerId: action.playerId,
    });
  }

  const secretSin = state.selectionsByPlayerId[action.playerId];
  if (secretSin !== action.choice.sin) {
    return fail("Revealed sin does not match the secretly selected sin", {
      secretSin,
      revealedSin: action.choice.sin,
    });
  }

  switch (action.choice.sin) {
    case "greed":
      return validateGreedChoice(state, action.choice);
    case "gluttony":
      return validateGluttonyChoice(state, action.choice);
    case "envy":
      return ok;
    case "lust":
      return validateLustChoice(state, action.choice);
    case "pride":
      return ok;
    case "wrath":
      return validateWrathChoice(state, action.choice.target);
    case "sloth":
      return ok;
  }
}
